import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useCreatePlan } from '../../../hooks/subscriptions';
import { useSnackbar } from '../../../components/SnackbarProvider';
import { BillingInterval, Currency } from '../../../types/subscriptions';
import { getCurrencySymbol } from '../../../utils/subscriptions';
import type { CreatePlanDto } from '../../../types/subscriptions';

export const PlanCreate = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const createMutation = useCreatePlan();

  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(Currency.DZD);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreatePlanDto>({
    defaultValues: {
      billingInterval: BillingInterval.MONTHLY,
      currency: Currency.DZD,
      isActive: true,
      displayOrder: 0,
      maxUsers: null,
      maxStores: null,
      maxWarehouses: null,
      metadata: {},
    },
  });

  const onSubmit = (data: CreatePlanDto) => {
    // Convert empty strings to null for numeric fields
    const payload = {
      ...data,
      maxUsers: !data.maxUsers || data.maxUsers === 0 ? null : Number(data.maxUsers),
      maxStores: !data.maxStores || data.maxStores === 0 ? null : Number(data.maxStores),
      maxWarehouses: !data.maxWarehouses || data.maxWarehouses === 0 ? null : Number(data.maxWarehouses),
    };

    createMutation.mutate(payload, {
      onSuccess: () => {
        showMessage('Plan created successfully', 'success');
        navigate('/subscriptions/plans');
      },
      onError: (error: any) => {
        showMessage(error.response?.data?.message || 'Failed to create plan', 'error');
      },
    });
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Create Subscription Plan
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('name', { required: 'Plan name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  label="Plan Name"
                  placeholder="e.g., Professional"
                  autoFocus
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('slug', {
                    required: 'Slug is required',
                    pattern: {
                      value: /^[a-z0-9-]+$/,
                      message: 'Slug must be lowercase letters, numbers, and hyphens only'
                    }
                  })}
                  error={!!errors.slug}
                  helperText={errors.slug?.message || 'URL-friendly identifier (e.g., professional)'}
                  fullWidth
                  label="Slug"
                  placeholder="e.g., professional"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('description', { required: 'Description is required' })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  placeholder="Describe the plan features and benefits"
                />
              </Grid>

              {/* Pricing */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Pricing
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('basePrice', {
                    required: 'Base price is required',
                    min: { value: 0, message: 'Price must be positive' }
                  })}
                  error={!!errors.basePrice}
                  helperText={errors.basePrice?.message}
                  fullWidth
                  label="Base Price"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{getCurrencySymbol(selectedCurrency)}</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Currency"
                        onChange={(e) => {
                          field.onChange(e);
                          setSelectedCurrency(e.target.value as Currency);
                        }}
                      >
                        <MenuItem value={Currency.DZD}>DZD - Algerian Dinar</MenuItem>
                        <MenuItem value={Currency.EUR}>EUR - Euro</MenuItem>
                        <MenuItem value={Currency.USD}>USD - US Dollar</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth error={!!errors.billingInterval}>
                  <InputLabel>Billing Interval</InputLabel>
                  <Controller
                    name="billingInterval"
                    control={control}
                    rules={{ required: 'Billing interval is required' }}
                    render={({ field }) => (
                      <Select {...field} label="Billing Interval">
                        <MenuItem value={BillingInterval.MONTHLY}>Monthly</MenuItem>
                        <MenuItem value={BillingInterval.YEARLY}>Yearly</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.billingInterval && (
                    <FormHelperText>{errors.billingInterval.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Resource Limits */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Resource Limits
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Leave at 0 for unlimited
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('maxUsers')}
                  fullWidth
                  label="Max Users"
                  type="number"
                  inputProps={{ min: 0 }}
                  helperText="0 = Unlimited"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('maxStores')}
                  fullWidth
                  label="Max Stores"
                  type="number"
                  inputProps={{ min: 0 }}
                  helperText="0 = Unlimited"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('maxWarehouses')}
                  fullWidth
                  label="Max Warehouses"
                  type="number"
                  inputProps={{ min: 0 }}
                  helperText="0 = Unlimited"
                />
              </Grid>

              {/* Settings */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('displayOrder', {
                    min: { value: 0, message: 'Display order must be non-negative' }
                  })}
                  error={!!errors.displayOrder}
                  helperText={errors.displayOrder?.message || 'Lower numbers appear first'}
                  fullWidth
                  label="Display Order"
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Active (available for assignment)"
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Box display="flex" gap={2} mt={4}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Plan'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/subscriptions/plans')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
